/**
 * Mensagens humanas para as telas PÚBLICAS e para os PORTAIS (cliente e
 * terceiro) — FASE-C.
 *
 * Motivo (diagnóstico UI/UX §6): o portal e as telas de senha do cliente
 * interpolavam `Erro: ${err.message}` direto na tela. Quem lê é o síndico ou o
 * porteiro do condomínio: "HTTP 500", "credenciais-invalidas" ou o texto de uma
 * exceção de rede não significam nada pra ele — e vazam vocabulário interno.
 *
 * Regra desta rodada: **nenhuma tela fora do sistema interno mostra o texto de
 * um erro cru**. Traduz-se o STATUS (que a gente controla) para uma frase em
 * pt-BR que diz o que aconteceu e o que fazer. O detalhe técnico continua
 * disponível pra quem precisa dele (console do dev / log do servidor).
 *
 * Uso:
 *   catch (err) { setErro(mensagemErro(err, 'Não foi possível abrir o chamado.')) }
 *   <Alert variant="danger">{mensagemErro(error, MSG.listaNaoCarregou)}</Alert>
 */

/** Erros de API do projeto expõem `status`; qualquer outro erro cai no fallback. */
function statusDe(err: unknown): number | null {
  if (typeof err === 'object' && err !== null && 'status' in err) {
    const s = (err as { status: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return null;
}

/** Falha de rede (fetch rejeitado antes de haver resposta HTTP). */
function pareceQuedaDeRede(err: unknown): boolean {
  return err instanceof TypeError || (err instanceof Error && /fetch|network/i.test(err.message));
}

export const MSG = {
  semPermissao: 'Você não tem acesso a esta área. Se precisar dela, fale com a IGM.',
  semConexao: 'Não conseguimos falar com o servidor. Confira sua conexão e tente de novo.',
  instavel: 'Tivemos um problema por aqui. Tente de novo em alguns instantes.',
  muitasTentativas: 'Recebemos muitas tentativas seguidas. Aguarde um minuto e tente de novo.',
  naoEncontrado: 'Não encontramos o que você procurava. Talvez o link esteja desatualizado.',
} as const;

/**
 * Traduz qualquer erro numa frase apresentável ao cliente.
 *
 * @param err      erro capturado (ClienteApiError, TerceiroApiError, ApiError, Error…)
 * @param fallback frase específica da tela pra quando o status não diz nada
 *                 (ex.: 'Não foi possível carregar seus chamados agora.')
 */
export function mensagemErro(err: unknown, fallback: string = MSG.instavel): string {
  if (pareceQuedaDeRede(err)) return MSG.semConexao;

  const status = statusDe(err);
  if (status === 401) return 'Sua sessão expirou. Entre de novo para continuar.';
  if (status === 403) return MSG.semPermissao;
  if (status === 404) return MSG.naoEncontrado;
  if (status === 429) return MSG.muitasTentativas;
  if (status === 503) return 'Esta área está temporariamente indisponível. Tente mais tarde.';
  if (status != null && status >= 500) return MSG.instavel;

  return fallback;
}
